import React from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import * as Yup from "yup";
import { RCCInput, RCCHookToast, RCCButton, RCCServicesApi, RCCGrid } from "react-common-components";
import { useForm } from "react-hook-form";
import {getValidationErrors, standardErrorMessages} from "src/utils/errors";
import { useTranslation } from "src/context/Translations/context";
import LoginScreen from "src/components/LoginScreen";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import Title from "src/components/Title";
import Loader from "src/components/Loader";
import { IUsersForgotPasswordSubmitInfos } from "./interface";

const ForgotPasswordSubmit: React.FC = () => {
	const { translate } = useTranslation();
	const { register, setError, clearErrors, handleSubmit, formState: { errors } } = useForm();
	const { addToast } = RCCHookToast();
	const history = useHistory();

	const params = useParams<{
        key: string;
    }>();
	
	const {data , loading, errorFetching } = useEffectFetchData<IUsersForgotPasswordSubmitInfos> (
		"UserPublic/GetForgotPasswordInfos", 
		{ key: params.key }
	);
	
	const submitForm = async (dt: any) :Promise<void> => {
		try
		{
			if(dt.email !== data.efa013Email){
				addToast({
					color: "yellow",
					description: translate("Você está tentando alterar a senha de um usuário utilizando o email diferente do permitido para esse link"),
					title: translate("Atenção")
				});
				return;
			}
			clearErrors();

			const schema = Yup.object().shape({
				email: Yup.string().email().required(translate("Email é obrigatório")),
				password: Yup.string().min(8, translate("Senha deve ter no mínimo 8 dígitos")).matches(/(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)/, translate("Deve conter 8 caracteres, um maiúsculo, um minúsculo, um número e um caractere especial")),
				password_confirmation: Yup.string().oneOf([Yup.ref("password")], translate("As senhas não conferem"))
			});

			await schema.validate(dt, {
				abortEarly: false
			});

			await RCCServicesApi.postRequest({
				method: "UserPublic/ForgotPasswordSubmit",
				body: {
					email: dt.email,
					password: dt.password
				}
			});

			addToast({
				title: translate("Sucesso"),
				description: translate("Senha alterada com sucesso"),
				color: "green"
			});

			history.push("/login");
		}
		catch(err)
		{
			if(getValidationErrors(err, setError))
				return;

			standardErrorMessages(err, translate("Verifique os campos e tente novamente"), addToast);
		}
	};

	return (
		<LoginScreen contentPosition="start">
			{loading ? (
				<Loader />
			):(
				<>
					{errorFetching || !data ? (
						<Title color="defaultColor">
							{translate("Esse link de recuperação de senha é invalido")}
						</Title>) : (
						<>
							<form onSubmit={handleSubmit(submitForm)}>
								<h1>{translate("Recuperar senha")}</h1>
								<RCCInput 
									name="email"
									type="email" 
									label={translate("Email")}
									register={register} 
									error={errors.email}
									required
									layout="outlined"
									autoComplete="off"
									icon={{
										name: "user",
										color: "var(--purple-lighter)"
									}}
								/>
								<RCCGrid container item>
									<RCCInput 
										name="password"
										type="password"
										register={register}
										label={translate("Senha")}
										maxLength={30}
										required
										error={errors.password}
										layout="outlined"
										autoComplete="off"
										icon={{
											name: "lock",
											color: "var(--purple-lighter)"
										}}
									/>
								</RCCGrid>
								<RCCGrid container item>
									<RCCInput 
										name="password_confirmation"
										type="password"
										register={register}
										label={translate("Confirmar senha")}
										maxLength={30}
										required
										error={errors.password_confirmation}
										layout="outlined"
										autoComplete="off"
										icon={{
											name: "lock",
											color: "var(--purple-lighter)"
										}}
									/>
								</RCCGrid>
								<br />
								<RCCButton 
									color="purple" 
									type="submit" 
									styleInline={`
												padding: 24px;
												width: 100%;
												>span.span-of-component-button{
													font-size: 1rem;
													font-weight: bold;
												}
											`}
								>
									{translate("Recuperar senha")}
								</RCCButton>
							</form>
		
							<Link to="/login">{translate("Voltar")}</Link>
						</>
					)}
				</>
			)}
		</LoginScreen>
	);
};

export default ForgotPasswordSubmit;
